import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getQaConfiguration, readableApiError, saludV2Client } from "./saludV2Client";
import { clearAttempt, readAttempt, writeAttempt } from "./captureAttemptStorage";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

function attemptKey() {
  if (typeof window.crypto?.randomUUID === "function") return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return hex.slice(0, 8) + "-" + hex.slice(8, 12) + "-" + hex.slice(12, 16) + "-" + hex.slice(16, 20) + "-" + hex.slice(20);
}

export function useSaludV2Capture(familia, onHydrate, { onCreated } = {}) {
  const location = useLocation();
  const initial = useRef(new URLSearchParams(location.search));
  const initialContext = initial.current.get("convocatoriaId") || location.state?.convocatoriaId || "";
  const [convocatoriaId, setConvocatoriaId] = useState(String(initialContext));
  const [verifiedContext, setVerifiedContext] = useState("");
  const [list, setList] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [record, setRecord] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [hasPendingSnapshot, setHasPendingSnapshot] = useState(false);
  const attempt = useRef(null);
  const inFlight = useRef(false);
  const initialized = useRef(false);
  const restored = useRef(false);
  const qa = getQaConfiguration();
  const parsedId = /^[1-9]\d*$/.test(convocatoriaId) ? Number(convocatoriaId) : NaN;
  const validContext = Number.isSafeInteger(parsedId) && parsedId > 0;

  const warn = useCallback((error) => {
    setNotice({ severity: "error", text: readableApiError(error) });
    if (error?.response?.status === 409) setConflict(true);
  }, []);

  const restorePending = useCallback(async (id, currentRecord = null) => {
    if (!onCreated || restored.current) return;
    restored.current = true;
    const pending = readAttempt(familia, id);
    if (!pending) return;
    let user;
    try { user = JSON.parse(localStorage.getItem("token")); } catch (_) { return; }
    let previous = pending.id ? currentRecord : null;
    if (pending.id && previous?.id !== pending.id) previous = (await saludV2Client.get(pending.id)).data;
    if (previous && (previous.familia !== familia || Number(previous.convocatoriaId) !== id)) {
      throw new Error("El envio pendiente no corresponde a esta convocatoria.");
    }
    if (!canWriteCapture(user?.rol, user?.id, previous)) return;
    attempt.current = pending;
    setHasPendingSnapshot(true);
    setRecord(previous);
    onHydrate(pending.body.datos);
    setDirty(true);
    setUncertain(true);
    setNotice({ severity: "warning", text: "Hay un envio sin confirmar. Reintente la misma operacion antes de otra captura." });
  }, [familia, onCreated, onHydrate]);

  const loadList = useCallback(async (append = false) => {
    if (inFlight.current) return;
    if (!validContext) {
      setNotice({ severity: "error", text: "Indique una convocatoria válida." });
      return;
    }
    inFlight.current = true;
    setBusy(true);
    try {
      const params = { convocatoriaId: parsedId, familia, limit: 25 };
      if (statusFilter !== "") params.estatus = statusFilter;
      if (append && nextCursor) params.cursor = nextCursor;
      const response = await saludV2Client.list(params);
      setList((previous) => append ? [...previous, ...response.data] : response.data);
      setNextCursor(response.page?.nextCursor || null);
      setVerifiedContext(String(parsedId));
      setNotice(null);
      await restorePending(parsedId);
    } catch (error) {
      setVerifiedContext("");
      warn(error);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [familia, nextCursor, parsedId, statusFilter, validContext, warn, restorePending]);

  const open = useCallback(async (uuid) => {
    if (inFlight.current || uncertain) return;
    if (!UUID.test(uuid)) {
      setNotice({ severity: "error", text: "Indique un UUID de atención válido." });
      return;
    }
    if (dirty && !window.confirm("Hay cambios sin guardar. ¿Desea descartarlos?")) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const { data: next } = await saludV2Client.get(uuid);
      if (next.familia !== familia || (convocatoriaId !== "" && String(next.convocatoriaId) !== String(parsedId))) {
        throw new Error("La atención no corresponde a esta convocatoria y formulario.");
      }
      onHydrate(next.datos);
      setConvocatoriaId(String(next.convocatoriaId));
      setVerifiedContext(String(next.convocatoriaId));
      setRecord(next);
      setHasPendingSnapshot(false);
      setDirty(false);
      setConflict(false);
      setNotice(null);
      attempt.current = null;
      await restorePending(Number(next.convocatoriaId), next);
    } catch (error) {
      warn(error);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [convocatoriaId, dirty, familia, onHydrate, parsedId, uncertain, warn, restorePending]);

  useEffect(() => {
    if (initialized.current || !qa.enabled) return;
    initialized.current = true;
    const uuid = initial.current.get("uuid");
    if (uuid) open(uuid);
    else if (validContext) loadList();
  }, [loadList, open, qa.enabled, validContext]);

  const save = useCallback(async (datos) => {
    if (inFlight.current) return;
    if (!validContext || verifiedContext !== String(parsedId)) {
      setNotice({ severity: "error", text: "Primero valide y cargue una convocatoria real." });
      return;
    }
    if (conflict) return;
    if (record && Number(record.convocatoriaId) !== parsedId) {
      setNotice({ severity: "error", text: "La atención pertenece a otra convocatoria." });
      return;
    }
    inFlight.current = true;
    setBusy(true);
    try {
      if (!attempt.current) {
        const body = record
          ? { expectedVersion: record.version, schemaVersion: 1, datos }
          : { familia, convocatoriaId: parsedId, schemaVersion: 1, datos };
        attempt.current = { key: attemptKey(), body: JSON.parse(JSON.stringify(body)), id: record?.id };
      }
      const currentAttempt = attempt.current;
      if (onCreated) writeAttempt(familia, parsedId, currentAttempt);
      let next;
      if (currentAttempt.id) {
        next = (await saludV2Client.update(currentAttempt.id, currentAttempt.body)).data;
      } else {
        const created = await saludV2Client.create(currentAttempt.body, currentAttempt.key);
        if (onCreated) {
          const receipt = created.data;
          if (created.httpStatus !== 201 || !UUID.test(receipt?.id) || receipt.familia !== familia ||
            Number(receipt.convocatoriaId) !== parsedId || !Number.isInteger(receipt.version) ||
            receipt.version < 1 || Number(receipt.estatus?.code) !== 0) {
            throw new Error("No se pudo confirmar el resultado del envio. Conserve los datos y reintente.");
          }
          clearAttempt(familia, parsedId);
          attempt.current = null;
          setDirty(false);
          setUncertain(false);
          setConflict(false);
          onCreated({ id: receipt.id, familia: receipt.familia, convocatoriaId: receipt.convocatoriaId,
            version: receipt.version, estatus: { code: 0, key: "PENDING" } });
          return;
        }
        next = (await saludV2Client.get(created.data.id)).data;
      }
      onHydrate(next.datos);
      setRecord(next);
      setHasPendingSnapshot(false);
      setDirty(false);
      setUncertain(false);
      setConflict(false);
      attempt.current = null;
      if (onCreated) clearAttempt(familia, parsedId);
      const state = { 0: "pendiente", 1: "verificado", 3: "error" }[next.estatus.code];
      setNotice({ severity: "success", text: (currentAttempt.id ? "Atención actualizada. Estado: " : "Atención guardada. Estado: ") + state + "." });
    } catch (error) {
      const status = error?.response?.status || error?.status;
      // A timeout/5xx may occur after commit. Freeze inputs and replay the identical attempt.
      if (!status || status >= 500) setUncertain(true);
      else {
        attempt.current = null;
        if (onCreated) clearAttempt(familia, parsedId);
        setUncertain(false);
      }
      warn(error);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [conflict, familia, onHydrate, parsedId, record, validContext, verifiedContext, warn, onCreated]);

  const newCapture = () => {
    if (inFlight.current || uncertain) return;
    if (dirty && !window.confirm("Hay cambios sin guardar. ¿Desea descartarlos?")) return;
    onHydrate({});
    setRecord(null);
    setHasPendingSnapshot(false);
    setDirty(false);
    setConflict(false);
    setNotice(null);
    attempt.current = null;
  };

  useEffect(() => {
    if (!dirty && !uncertain) return undefined;
    const beforeUnload = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty, uncertain]);

  return {
    qa, convocatoriaId, changeContext(value) {
      if (record || dirty || uncertain || inFlight.current) return;
      setConvocatoriaId(value);
      restored.current = false;
      setHasPendingSnapshot(false);
      setVerifiedContext("");
      setList([]);
      setNextCursor(null);
    },
    loadList, list, nextCursor, statusFilter, setStatusFilter(value) {
      if (inFlight.current) return;
      setStatusFilter(value);
      setList([]);
      setNextCursor(null);
    },
    open, save, record, busy, notice, setNotice, conflict, dirty, setDirty,
    uncertain, retry: () => save(attempt.current?.body.datos), newCapture,
    hasPendingSnapshot,
    contextReady: verifiedContext === String(parsedId) && validContext,
  };
}

export function canWriteCapture(role, userId, record) {
  const value = String(role || "");
  if (["1", "2", "6"].includes(value)) return true;
  if (value !== "11") return false;
  if (!record) return true;
  return Number(record.estatus?.code) === 0 && Number(userId) > 0
    && Number(record.transcriptorUsuarioId) === Number(userId);
}
